import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Badge } from "@/lib/ui";
import { ShieldCheck, Upload, Clock, XCircle } from "lucide-react";

const MAX_BYTES = 1.5 * 1024 * 1024;

function FilePick({ label, hint, value, onChange, testid, required }) {
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose a JPG or PNG image."); return; }
    if (f.size > MAX_BYTES) { toast.error("Image must be under 1.5 MB. Try a smaller photo."); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(f);
  };
  return (
    <div>
      <label className="block font-semibold text-slate-800 mb-2">{label} {required && <span className="text-red-500">*</span>}</label>
      <p className="text-sm text-slate-500 mb-2">{hint}</p>
      <label className="flex items-center gap-3 border-2 border-dashed border-[#E2D9CE] hover:border-[#D96B43] rounded-xl p-4 cursor-pointer min-h-[64px] transition-colors">
        <Upload className="w-6 h-6 text-[#D96B43]" />
        <span className="font-medium text-slate-700">{value ? "Change photo" : "Choose a photo"}</span>
        <input data-testid={testid} type="file" accept="image/*" className="hidden" onChange={pick} />
      </label>
      {value && <img src={value} alt={label} className="mt-3 max-h-40 rounded-xl border border-[#E2D9CE]" />}
    </div>
  );
}

export default function VerificationCard({ user, onUpdated }) {
  const [v, setV] = useState(undefined);
  const [idDoc, setIdDoc] = useState("");
  const [bgDoc, setBgDoc] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/helpers/verification/mine").then((r) => setV(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!idDoc) { toast.error("Please upload a photo of your ID."); return; }
    setLoading(true);
    try {
      await api.post("/helpers/verification", { id_document: idDoc, background_document: bgDoc, note });
      toast.success("Documents sent! Our team will review them shortly.");
      setIdDoc(""); setBgDoc(""); setNote("");
      await load();
      onUpdated?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (v === undefined) return null;
  const showForm = !user.is_verified && (!v || v.status === "rejected");

  return (
    <section data-testid="verification-card" className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 shadow-sm mt-8">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-7 h-7 text-[#2E7D6B]" />
        <h2 className="text-2xl font-display font-bold text-slate-900">Trust & verification</h2>
      </div>
      <p className="text-slate-600 mb-5">Verified helpers earn trust badges and are shown first to care seekers.</p>

      {user.is_verified && (
        <div data-testid="verification-approved" className="flex flex-wrap gap-2">
          <Badge color="teal"><ShieldCheck className="w-4 h-4" /> ID Verified</Badge>
          {user.background_checked && <Badge color="green">Background Checked</Badge>}
        </div>
      )}
      {!user.is_verified && v?.status === "pending" && (
        <div data-testid="verification-pending" className="flex items-center gap-3 bg-[#FFF7E6] text-[#A9741A] rounded-xl p-4 font-medium">
          <Clock className="w-6 h-6" /> Your documents are under review. We'll update your badge soon.
        </div>
      )}
      {!user.is_verified && v?.status === "rejected" && (
        <div data-testid="verification-rejected" className="flex items-start gap-3 bg-red-50 text-red-700 rounded-xl p-4 mb-5">
          <XCircle className="w-6 h-6 shrink-0" />
          <div><p className="font-semibold">Your last submission was not approved.</p>{v.admin_note && <p className="mt-1">{v.admin_note}</p>}<p className="mt-1">You can upload new documents below.</p></div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="space-y-5">
          <FilePick required label="Government ID" hint="A clear photo of your passport, driver's licence or national ID." value={idDoc} onChange={setIdDoc} testid="verification-id-input" />
          <FilePick label="Background check (optional)" hint="A police clearance or background check certificate, if you have one." value={bgDoc} onChange={setBgDoc} testid="verification-bg-input" />
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Note for our team (optional)</label>
            <textarea data-testid="verification-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] outline-none" />
          </div>
          <button data-testid="verification-submit-btn" disabled={loading}
            className="w-full bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-semibold min-h-[52px] rounded-xl text-lg transition-colors">
            {loading ? "Sending…" : "Submit for verification"}
          </button>
        </form>
      )}
    </section>
  );
}
